/**
 * System Context Utility
 * Determines the system context (VSAT or THPT) based on the app configuration
 */

/**
 * Get the system context for the current app
 * - VSAT student app: 'VSAT'
 * - THPT app: 'THPT'
 * - Portal: Can be either based on user selection
 *
 * @returns 'VSAT' | 'THPT'
 */
/**
 * Get the system context for the current app
 * - VSAT student app: 'VSAT' (set via VITE_SYSTEM_CONTEXT=VSAT)
 * - THPT app: 'THPT' (set via VITE_SYSTEM_CONTEXT=THPT)
 * - Portal: Can be either based on user selection
 *
 * IMPORTANT: Each app MUST set VITE_SYSTEM_CONTEXT in their .env file
 * This ensures systemContext is correct regardless of port, hostname, or deployment environment
 *
 * @returns 'VSAT' | 'THPT'
 */
export function getSystemContext(): 'VSAT' | 'THPT' {
  const envSystemContext = import.meta.env.VITE_SYSTEM_CONTEXT;
  const requireSystemContext = import.meta.env.VITE_REQUIRE_SYSTEM_CONTEXT === 'true';

  // Explicit env wins
  if (envSystemContext === 'THPT' || envSystemContext === 'VSAT') {
    return envSystemContext;
  }

  // If projects opt in to strict mode (THPT app should), fail fast when env is missing
  if (requireSystemContext) {
    throw new Error(
      '[getSystemContext] VITE_SYSTEM_CONTEXT is required but missing. Please set it to VSAT or THPT in your .env file.'
    );
  }

  // Fallback: infer from hostname for backward compatibility
  if (typeof window !== 'undefined') {
    const hostname = window.location.hostname;
    if (hostname.includes('thpt.') || hostname.includes('thpt-')) {
      console.warn(
        '[getSystemContext] Using hostname detection (THPT). Please set VITE_SYSTEM_CONTEXT in .env file'
      );
      return 'THPT';
    }
  }

  // Default to VSAT for student app (backward compatibility)
  console.warn(
    '[getSystemContext] VITE_SYSTEM_CONTEXT not set. Defaulting to VSAT. Please set VITE_SYSTEM_CONTEXT=VSAT in .env file and restart dev server'
  );
  return 'VSAT';
}
